import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import styled, { keyframes } from "styled-components";
import { SVGMap } from "react-svg-map";

import { colors } from "../../../../theme";
import France from "../../../../assets/svg-maps/france.departments";
import data_v1 from "./data.json";
import data_v2 from "./data_extended.json";

const Container = styled.div`
  display: inline-block;
  padding-bottom: 169px;
  position: relative;
  transition: 0.3s;
  width: ${(props) => {
    switch (props.zoomState) {
      case "zoomed":
        return "80%";
      case "minimized":
        return "20%";
      default:
        return "50%";
    }
  }};

  @media (max-width: 767px) {
    margin-bottom: 60px;
    padding-bottom: 166px;
    width: 100%;
  }

  > div:first-of-type {
    position: relative;
  }

  svg {
    stroke: white;
    fill: white;
  }

  path {
    transition: 0.3s;

    &:hover {
      opacity: 0.7;
    }
  }
`;

const Legend = styled.div`
  color: ${colors.textColor};
  left: 50%;
  margin-top: 15px;
  opacity: ${(props) => (props.isHidden ? 0 : 1)};
  position: absolute;
  transform: translateX(-50%);
  width: 75%;

  @media (max-width: 1599px) {
    width: 90%;
  }

  h1 {
    color: black;
    font-size: 1.6rem;
    font-weight: 600;
    margin-bottom: 8px;

    @media (max-width: 767px) {
      font-size: 1.5rem;
      margin-bottom: 5px;
    }
  }

  ul {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  li {
    font-size: 1.3rem;
    display: inline-block;
    line-height: 2.4rem;
    width: 50%;
  }

  p {
    font-size: 1.3rem;
    line-height: 2rem;
    margin: 10px 0 0;
  }
`;

const Color = styled.span`
  background: ${(props) => props.color};
  border-radius: 4px;
  display: inline-block;
  height: 12px;
  margin-right: 5px;
  vertical-align: -2px;
  width: 12px;
`;

const Zoom = styled.button`
  bottom: 40%;
  display: ${(props) => (props.isHidden ? "none" : "inline")};
  font-size: 2.4rem;
  left: 8%;
  position: absolute;
  transition: 0.3s;

  @media (max-width: 767px) {
    display: none;
  }

  :hover {
    color: ${colors.primaryColor};
  }
`;

const appear = keyframes`
  from {
    opacity: 0;
    margin-top: 15px;
  }

  to {
    opacity: 1;
    margin-top: 0;
  }
`;
const Tooltip = styled.div.attrs((props) => ({
  style: {
    top: props.top + "px",
    left: props.left + "px",
  },
}))`
  animation: ${appear} 0.2s linear;
  background: white;
  border-radius: 4px;
  box-shadow: 0 4px 11px rgba(0, 0, 0, 0.1);
  color: ${colors.textColor};
  font-size: 1.6rem;
  line-height: 1.15;
  padding: 14px 14px 8px;
  position: fixed;
  transform: translate(-50%, -100%);
  z-index: 10;

  h1 {
    color: black;
    font-size: 1.2rem;
    font-weight: 600;
    line-height: 1.15rem;
    margin-bottom: 5px;
    text-transform: uppercase;
  }

  p {
    margin: 0;
  }

  span {
    color: ${(props) => props.color};
    font-weight: 500;
  }
`;

const Places = styled.ul`
  font-size: 1.5rem;
  list-style: inside;
  margin: 6px 0;
  padding: 0;

  li {
    margin-bottom: 4px;
  }
`;

function CovidLethalityMap({
  version,
  type,
  typeLabel,
  legend,
  formatTooltipValue,
  getLocationColor,
  scaleColor,
  zoomState,
  toggleZoom,
}) {
  const [tooltipContent, setTooltipContent] = useState(null);
  let data;
  switch (version) {
    case "v1":
      data = data_v1;
      break;
    case "v2":
    default:
      data = data_v2;
      break;
  }

  useEffect(() => {
    Object.keys(data).forEach((id) => {
      const el = document.querySelector(`.${type}_${version} path[id="${id}"]`);
      if (el) {
        el.style.fill = getLocationColor(data[id][type]);
      }
    });
  }, [type, getLocationColor, data, version]);

  function handleLocationMouseOver(e) {
    const target = data[e.target.id];
    setTooltipContent({
      title: `${e.target.attributes.name.value} (${e.target.id}) `,
      content: formatTooltipValue(target[type]),
      places: {
        hospital: formatTooltipValue(target.diff_Hopital_Clinique),
        retirement: formatTooltipValue(target.diff_Maison_retraite),
        home: formatTooltipValue(target.diff_Domicile),
      },
      color: getLocationColor(target[type]),
      top: e.clientY - 15,
      left: e.clientX,
    });
  }

  function handleLocationMouseMove(e) {
    const { clientY, clientX } = e;
    setTooltipContent((state) => ({
      ...state,
      top: clientY - 15,
      left: clientX,
    }));
  }

  function handleLocationMouseOut() {
    setTooltipContent(null);
  }

  return (
    <Container zoomState={zoomState}>
      <div>
        <SVGMap
          className={type + "_" + version}
          onLocationMouseOver={handleLocationMouseOver}
          onLocationMouseMove={handleLocationMouseMove}
          onLocationMouseOut={handleLocationMouseOut}
          map={France}
        />
        <Zoom onClick={toggleZoom} isHidden={zoomState === "minimized"}>
          <i
            className={`far fa-search-${
              zoomState === "zoomed" ? "minus" : "plus"
            }`}
          />
        </Zoom>
      </div>

      <Legend isHidden={zoomState === "minimized"}>
        <h1>{legend.title}</h1>
        <ul>
          {legend.categories.map((category, i) => (
            <li key={category}>
              <Color color={scaleColor(i / legend.categories.length)} />{" "}
              {category}
            </li>
          ))}
        </ul>
        <p>{legend.note}</p>
      </Legend>

      {tooltipContent && (
        <Tooltip
          color={tooltipContent.color}
          top={tooltipContent.top}
          left={tooltipContent.left}
        >
          <h1>{tooltipContent.title}</h1>
          <p>
            {typeLabel} :{" "}
            <span>
              {tooltipContent.content !== null
                ? tooltipContent.content
                : "Non référencé"}
            </span>
          </p>
          {version !== "v1" && type === "variation_2019_2020" && (
            <Places>
              <li>
                Hôpital / Clinique :{" "}
                <span style={{ color: colors.green }}>
                  {tooltipContent.places.hospital}
                </span>
              </li>
              <li>
                Maison de retraite :{" "}
                <span style={{ color: colors.purple }}>
                  {tooltipContent.places.retirement}
                </span>
              </li>
              <li>
                Domicile :{" "}
                <span style={{ color: colors.red }}>
                  {tooltipContent.places.home}
                </span>
              </li>
              <li>
                Autre :{" "}
                <span style={{ color: colors.gray }}>
                  {tooltipContent.content -
                    tooltipContent.places.hospital -
                    tooltipContent.places.retirement -
                    tooltipContent.places.home}
                </span>
              </li>
            </Places>
          )}
        </Tooltip>
      )}
    </Container>
  );
}

CovidLethalityMap.propTypes = {
  version: PropTypes.oneOf(["v1", "v2"]).isRequired,
  type: PropTypes.oneOf(["variation_2019_2020", "deces"]).isRequired,
  typeLabel: PropTypes.string.isRequired,
  legend: PropTypes.shape({
    title: PropTypes.string.isRequired,
    categories: PropTypes.array.isRequired,
    note: PropTypes.string.isRequired,
  }).isRequired,
  formatTooltipValue: PropTypes.func.isRequired,
  getLocationColor: PropTypes.func.isRequired,
  scaleColor: PropTypes.func.isRequired,
  toggleZoom: PropTypes.func.isRequired,
  zoomState: PropTypes.oneOf(["normal", "zoomed", "minimized"]).isRequired,
};

CovidLethalityMap.defaultProps = {
  formatTooltipValue: (val) => val,
};

export default CovidLethalityMap;
